import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  MoreVertical, ShieldOff, ShieldCheck, PowerOff, Power, KeyRound,
  Building2, X, Check, Copy, UserCheck, ChevronDown, UserPlus,
} from 'lucide-react';

const ROLES = ['owner','md','project_manager','accountant','payments','site_engineer','viewer','vendor'];
const ROLE_LABEL = {
  owner: 'Owner', md: 'Managing Director', project_manager: 'Project Manager',
  accountant: 'Accountant', payments: 'Payments', site_engineer: 'Site Engineer',
  viewer: 'Viewer', vendor: 'Vendor (external)',
};
const SCOPED_ROLES = new Set(['project_manager', 'site_engineer', 'vendor']);

export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(null);
  const [openMenu, setOpenMenu] = useState(null); // user_id
  const [assignModal, setAssignModal] = useState(null);
  const [tempPassword, setTempPassword] = useState(null); // { user, temp }
  const [showInvite, setShowInvite] = useState(false);
  const load = () => Promise.all([
    api.get('/users'),
    api.get('/properties').catch(() => ({ data: [] })),
  ]).then(([u, p]) => { setUsers(u.data); setProperties(p.data || []); });
  useEffect(() => { load(); }, []);

  const isAdmin = me && ['owner', 'md'].includes(me.role);
  const propertyMap = Object.fromEntries(properties.map(p => [p.id, p]));

  const changeRole = async (uid, role) => {
    setSaving(uid);
    try {
      await api.patch(`/users/${uid}/role`, { role });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    } finally { setSaving(null); }
  };

  const toggleActive = async (u) => {
    const willActivate = !(u.is_active !== false);
    const msg = willActivate
      ? `Deactivate ${u.name}? They will lose access immediately and all sessions will be revoked.`
      : `Reactivate ${u.name}?`;
    if (!window.confirm(msg)) return;
    setSaving(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/toggle-active`, {});
      toast.success(r.data.is_active ? `${u.name} reactivated` : `${u.name} deactivated`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); setOpenMenu(null); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.name}? They must re-enrol on next login.`)) return;
    setSaving(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/reset-2fa`);
      toast.success(r.data.message);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); setOpenMenu(null); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}? Their sessions will be revoked and they'll need to sign in with the new temporary password.`)) return;
    setSaving(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/reset-password`);
      setTempPassword({ user: u, temp: r.data.temp_password });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(null); setOpenMenu(null); }
  };

  return (
    <div className="py-6 max-w-6xl mx-auto space-y-6" data-testid="team-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Team</div>
          <h1 className="font-serif text-4xl">Users & roles</h1>
          <p className="text-slate-600 mt-2 text-sm">
            {isAdmin
              ? 'Assign roles, reset 2FA, activate/deactivate accounts and scope users to specific properties.'
              : 'Only owner and MD can manage users. Read-only view for you.'}
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowInvite(true)}
            data-testid="team-invite-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800 transition-all hover:-translate-y-0.5">
            <UserPlus size={16}/> Invite user
          </button>
        )}
      </div>

      <div className="glass p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="overline text-left border-b border-slate-200/60">
              <th className="py-2">User</th>
              <th>Role</th>
              <th>Status</th>
              <th>2FA</th>
              <th>Property scope</th>
              {isAdmin && <th className="text-right pr-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isSelf = u.user_id === me?.user_id;
              const active = u.is_active !== false;
              const scoped = SCOPED_ROLES.has(u.role);
              const assignedCount = (u.assigned_property_ids || []).length;
              return (
                <tr key={u.user_id} className={`border-b border-slate-100/70 ${!active ? 'opacity-50' : ''}`} data-testid={`user-row-${u.user_id}`}>
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=1A2530&color=fff`}
                        alt="" className="w-9 h-9 rounded-full border border-white shadow-sm"
                      />
                      <div>
                        <div className="text-sm font-medium">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                        {isSelf && <div className="overline text-[0.55rem] text-emerald-600 mt-0.5">You</div>}
                      </div>
                    </div>
                  </td>
                  <td>
                    {isAdmin && !isSelf ? (
                      <select
                        disabled={saving === u.user_id}
                        value={u.role}
                        onChange={(e) => changeRole(u.user_id, e.target.value)}
                        data-testid={`role-select-${u.user_id}`}
                        className="px-2 py-1 rounded-full text-xs border border-slate-200 bg-white/80"
                      >
                        {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : (
                      <span className="status-pill status-gold text-xs">{ROLE_LABEL[u.role] || u.role}</span>
                    )}
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${active ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`user-active-${u.user_id}`}>
                      <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`}/>
                      {active ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td>
                    {u.totp_enabled
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck size={12}/> Enabled</span>
                      : <span className="text-xs text-slate-400">Not enrolled</span>}
                  </td>
                  <td>
                    {scoped ? (
                      <button
                        onClick={() => isAdmin && setAssignModal(u)}
                        disabled={!isAdmin}
                        data-testid={`user-scope-${u.user_id}`}
                        className={`text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${
                          assignedCount === 0
                            ? 'border-amber-200 bg-amber-50 text-amber-800'
                            : 'border-slate-200 bg-white/60 text-slate-700 hover:bg-white'
                        }`}>
                        <Building2 size={11}/>
                        {assignedCount === 0 ? 'All properties (unscoped)' : `${assignedCount} propert${assignedCount === 1 ? 'y' : 'ies'}`}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">All (role has full access)</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="text-right pr-2 relative">
                      {!isSelf && (
                        <>
                          <button
                            onClick={() => setOpenMenu(openMenu === u.user_id ? null : u.user_id)}
                            data-testid={`user-actions-${u.user_id}`}
                            className="p-2 rounded-full hover:bg-slate-100 text-slate-600"
                          >
                            <MoreVertical size={14}/>
                          </button>
                          {openMenu === u.user_id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)}/>
                              <motion.div
                                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                                className="absolute right-2 top-10 z-20 w-56 p-1.5 rounded-2xl bg-white shadow-xl border border-slate-200"
                                data-testid={`user-actions-menu-${u.user_id}`}
                              >
                                {u.totp_enabled && (
                                  <button onClick={() => reset2fa(u)} className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700" data-testid={`reset-2fa-${u.user_id}`}>
                                    <ShieldOff size={13} className="text-amber-600"/> Reset 2FA
                                  </button>
                                )}
                                {u.password_hash && (
                                  <button onClick={() => resetPassword(u)} className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700" data-testid={`reset-password-${u.user_id}`}>
                                    <KeyRound size={13} className="text-slate-600"/> Reset password
                                  </button>
                                )}
                                <button onClick={() => toggleActive(u)}
                                        className={`w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 flex items-center gap-2 text-sm ${active ? 'text-red-600' : 'text-emerald-700'}`}
                                        data-testid={`toggle-active-${u.user_id}`}>
                                  {active ? <><PowerOff size={13}/> Deactivate</> : <><Power size={13}/> Reactivate</>}
                                </button>
                              </motion.div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {users.length === 0 && <tr><td colSpan={isAdmin ? 6 : 5} className="py-8 text-center text-slate-400">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {assignModal && (
        <PropertyScopeModal
          user={assignModal}
          properties={properties}
          onClose={() => setAssignModal(null)}
          onSaved={async () => { setAssignModal(null); await load(); }}
        />
      )}

      {tempPassword && (
        <TempPasswordModal
          userInfo={tempPassword}
          onClose={() => setTempPassword(null)}
        />
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onCreated={async (result) => {
            setShowInvite(false);
            await load();
            setTempPassword({ user: { name: result.name, email: result.email }, temp: result.temp_password });
          }}
        />
      )}
    </div>
  );
}

function InviteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'viewer' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/team/invite', form);
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to invite');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <motion.form onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-md p-8"
        data-testid="team-invite-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline">Invite user</div>
            <h2 className="font-serif text-2xl">Add a team member</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
        </div>

        <p className="text-xs text-slate-500 mb-6">
          A temporary password will be generated. Share it with them via WhatsApp/email — they&apos;ll be forced to change it on first login.
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Full name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              required data-testid="invite-name-input"
              placeholder="Fatima Al Zaabi"
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-[#679286]/40"/>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Work email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              required data-testid="invite-email-input"
              placeholder="fatima@irisrealty.com"
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-[#679286]/40"/>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="invite-role-select"
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 text-sm">
              {ROLES.filter(r => r !== 'vendor').map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">Vendor accounts are created from the Vendor page.</div>
          </div>
        </div>

        <div className="flex gap-2 mt-8">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-full border border-slate-200 text-sm hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy}
            data-testid="invite-submit-btn"
            className="flex-1 py-2.5 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? 'Creating…' : <><UserPlus size={14}/> Create user</>}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function PropertyScopeModal({ user, properties, onClose, onSaved }) {
  const [ids, setIds] = useState(new Set(user.assigned_property_ids || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const next = new Set(ids);
    if (next.has(id)) next.delete(id); else next.add(id);
    setIds(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.user_id}/assign-properties`, { assigned_property_ids: Array.from(ids) });
      toast.success(`${user.name}'s access updated`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const clearAll = () => setIds(new Set());
  const selectAll = () => setIds(new Set(properties.map(p => p.id)));

  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
        data-testid="property-scope-modal"
      >
        <div className="overline mb-1">Access scope</div>
        <h2 className="font-serif text-2xl mb-2">Assign properties to {user.name}</h2>
        <p className="text-sm text-slate-600 mb-4">
          Empty list = access to <b>all properties</b> (unscoped). Selecting properties restricts this user to seeing only those.
        </p>

        <div className="flex gap-2 mb-3">
          <button onClick={clearAll} className="btn-ghost text-xs">Clear all (unscoped)</button>
          <button onClick={selectAll} className="btn-ghost text-xs">Select all</button>
          <span className="text-xs text-slate-500 ml-auto self-center">{ids.size}/{properties.length} selected</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6 max-h-96 overflow-y-auto">
          {properties.map(p => {
            const on = ids.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`flex items-center justify-between p-3 rounded-xl border text-left ${on ? 'bg-[#EEF2F0] border-[#679286]' : 'bg-white/70 border-slate-200 hover:bg-white'}`}
                data-testid={`scope-property-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 truncate">{p.community} · {p.stage}</div>
                </div>
                {on && <Check size={16} className="text-[#679286]"/>}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="save-scope-btn">
            {saving ? 'Saving…' : 'Save access'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function TempPasswordModal({ userInfo, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(userInfo.temp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-md p-8"
        data-testid="temp-password-modal"
      >
        <div className="overline mb-1 text-amber-800">One-time temp password</div>
        <h2 className="font-serif text-2xl mb-3">Copy this now</h2>
        <p className="text-sm text-slate-600 mb-4">
          Share this temp password with <b>{userInfo.user.name}</b> ({userInfo.user.email}) through a secure channel.
          It will NOT be shown again. They will be forced to change it on next login.
        </p>
        <div className="flex items-center gap-2 p-3 bg-slate-900 text-white rounded-xl font-mono text-lg tracking-wider mb-4">
          <span className="flex-1" data-testid="temp-password-value">{userInfo.temp}</span>
          <button onClick={copy} className="p-2 rounded-lg hover:bg-white/10" data-testid="temp-password-copy">
            {copied ? <Check size={16} className="text-emerald-400"/> : <Copy size={16}/>}
          </button>
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="btn-primary text-sm" data-testid="temp-password-close">I&apos;ve saved it</button>
        </div>
      </motion.div>
    </div>
  );
}
